import {defineStore} from "pinia";
import {Tag} from "@/store/clients";
import {JobsApi} from "@/ api/JobsApi";
import {WorkerApi} from "@/ api/WorkerApi";
import {TagsApi} from "@/ api/TagsApi";
import {TagEntityType} from "@/models/etc/types";
import {notifyFileJobBindingAdded} from "@/utils/fileJobBindings";

export type JobsContextType = 'main' | 'client' | 'project'

export type JobsContext = {
    type: JobsContextType,
    entityId: number | null,
    clientSearch: boolean,
    holdingSearch: boolean
}

export type JobsRefreshRequest = {
    type: JobsContextType,
    entityId: number | null
}

type JobsLastFilters = {
    textFilter: string | undefined,
    workerId: number | undefined,
    tags: number[] | null | undefined,
    isActual: boolean | undefined,
    isLost: boolean | undefined,
    count: number
}

type JobsState = {
    isJobsLoading: boolean,
    isLoadingMore: boolean,
    hasMoreBelow: boolean,
    hasMoreAbove: boolean,
    context: JobsContext,
    lastFilters: JobsLastFilters,
    jobsCount: JobsCount,
    jobs: Job[],
    workers: WorkerListItem[] | null,
    showUserJobsId: number | null,
    highlightedJobId: number | null,
    jobsRefreshRequest: JobsRefreshRequest | null
}

const DEFAULT_COUNT = 30

export const jobsStore = defineStore({
    id: 'jobsStore',
    state: () => ({
        isJobsLoading: false,
        isLoadingMore: false,
        hasMoreBelow: true,
        hasMoreAbove: false,
        context: { type: 'main', entityId: null, clientSearch: false, holdingSearch: false },
        lastFilters: {
            textFilter: undefined,
            workerId: undefined,
            tags: null,
            isActual: undefined,
            isLost: undefined,
            count: DEFAULT_COUNT,
        },
        jobsCount: {
            Actual: 0,
            Lost: 0
        },
        jobs: [],
        workers: null,
        showUserJobsId: null,
        highlightedJobId: null,
        jobsRefreshRequest: null
    } as JobsState),
    actions: {
        setContext(type: JobsContextType, entityId: number | null) {
            const prev = this.context
            const changed = prev.type !== type || prev.entityId !== entityId
            this.context = {
                type,
                entityId,
                clientSearch: changed ? false : prev.clientSearch,
                holdingSearch: changed ? false : prev.holdingSearch,
            }
            this.hasMoreBelow = true
            this.hasMoreAbove = false
        },
        setClientSearch(value: boolean) {
            if (this.context.type !== 'client') return
            if (this.context.clientSearch === value) return

            this.context.clientSearch = value
            this.hasMoreBelow = true
            this.hasMoreAbove = false
        },
        setHoldingSearch(value: boolean) {
            if (this.context.type !== 'client') return
            if (this.context.holdingSearch === value) return

            this.context.holdingSearch = value
            this.hasMoreBelow = true
            this.hasMoreAbove = false
        },
        async getJobsCount(textFilter?: string, workerId?: number, tags?: number[] | null) {
            const ctx = this.context
            if (ctx.type === 'client') {
                if (ctx.entityId == null) return
                this.jobsCount = await JobsApi.getJobsCountByClient(ctx.entityId, textFilter, workerId, tags, ctx.clientSearch, ctx.holdingSearch)
            } else if (ctx.type === 'project') {
                if (ctx.entityId == null) return
                this.jobsCount = await JobsApi.getJobsCountByProject(ctx.entityId, textFilter, workerId, tags)
            } else {
                this.jobsCount = await JobsApi.getJobsCount(textFilter, workerId, tags)
            }
        },
        async getJobs(
            textFilter?: string,
            workerId?: number,
            tags?: number[] | null,
            isActual?: boolean,
            isLost?: boolean,
            count?: number,
            mode?: number,
            skipFrom?: string
        ) {
            try {
                this.isJobsLoading = true
                const effectiveCount = count ?? DEFAULT_COUNT
                this.lastFilters = {
                    textFilter,
                    workerId,
                    tags,
                    isActual,
                    isLost,
                    count: effectiveCount,
                }
                this.hasMoreBelow = true
                this.hasMoreAbove = false
                this.highlightedJobId = null

                const ctx = this.context
                let result: Job[]
                if (ctx.type === 'client') {
                    if (ctx.entityId == null) return
                    result = await JobsApi.getJobsByClient(
                        ctx.entityId,
                        textFilter,
                        workerId,
                        tags,
                        isActual,
                        isLost,
                        ctx.clientSearch,
                        ctx.holdingSearch,
                        effectiveCount,
                        mode,
                        skipFrom
                    )
                } else if (ctx.type === 'project') {
                    if (ctx.entityId == null) return
                    result = await JobsApi.getJobsByProject(
                        ctx.entityId,
                        textFilter,
                        workerId,
                        tags,
                        isActual,
                        isLost,
                        effectiveCount,
                        mode,
                        skipFrom
                    )
                } else {
                    result = await JobsApi.getJobs(
                        textFilter,
                        workerId,
                        tags,
                        isActual,
                        isLost,
                        effectiveCount,
                        mode,
                        skipFrom
                    )
                }
                this.jobs = result
                if (result.length < effectiveCount) {
                    this.hasMoreBelow = false
                }
            } catch (e) {
                console.error(e)
            } finally {
                this.isJobsLoading = false
            }
        },
        async getJobsAroundOne(jobId: number) {
            try {
                this.isJobsLoading = true
                this.lastFilters = {
                    textFilter: undefined,
                    workerId: undefined,
                    tags: null,
                    isActual: undefined,
                    isLost: undefined,
                    count: DEFAULT_COUNT,
                }
                this.hasMoreBelow = true
                this.hasMoreAbove = true

                const ctx = this.context
                const clientId = ctx.type === 'client'
                    ? ctx.entityId ?? undefined
                    : undefined
                const projectId = ctx.type === 'project'
                    ? ctx.entityId ?? undefined
                    : undefined

                const result = await JobsApi.getJobsAround(jobId, clientId, projectId)
                this.jobs = result
                this.highlightedJobId = jobId
            } catch (e) {
                console.error(e)
            } finally {
                this.isJobsLoading = false
            }
        },
        clearHighlightedJob() {
            this.highlightedJobId = null
        },
        async loadMoreJobsBelow() {
            if (this.isLoadingMore || !this.hasMoreBelow || this.jobs.length === 0) return

            const last = this.jobs[this.jobs.length - 1]
            const skipFrom = last?.CreateTime
            if (!skipFrom) return

            try {
                this.isLoadingMore = true
                const { textFilter, workerId, tags, isActual, isLost, count } = this.lastFilters
                const ctx = this.context
                let result: Job[]
                if (ctx.type === 'client') {
                    if (ctx.entityId == null) return
                    result = await JobsApi.getJobsByClient(
                        ctx.entityId,
                        textFilter,
                        workerId,
                        tags,
                        isActual,
                        isLost,
                        ctx.clientSearch,
                        ctx.holdingSearch,
                        count,
                        1,
                        skipFrom
                    )
                } else if (ctx.type === 'project') {
                    if (ctx.entityId == null) return
                    result = await JobsApi.getJobsByProject(
                        ctx.entityId,
                        textFilter,
                        workerId,
                        tags,
                        isActual,
                        isLost,
                        count,
                        1,
                        skipFrom
                    )
                } else {
                    result = await JobsApi.getJobs(
                        textFilter,
                        workerId,
                        tags,
                        isActual,
                        isLost,
                        count,
                        1,
                        skipFrom
                    )
                }
                if (result.length === 0) {
                    this.hasMoreBelow = false
                } else {
                    this.jobs.push(...result)
                    if (result.length < count) {
                        this.hasMoreBelow = false
                    }
                }
            } catch (e) {
                console.error(e)
                this.hasMoreBelow = false
            } finally {
                this.isLoadingMore = false
            }
        },
        async loadMoreJobsAbove() {
            if (this.isLoadingMore || !this.hasMoreAbove || this.jobs.length === 0) return

            const first = this.jobs[0]
            const skipFrom = first?.CreateTime
            if (!skipFrom) return

            try {
                this.isLoadingMore = true
                const { textFilter, workerId, tags, isActual, isLost, count } = this.lastFilters
                const ctx = this.context
                let result: Job[]
                if (ctx.type === 'client') {
                    if (ctx.entityId == null) return
                    result = await JobsApi.getJobsByClient(
                        ctx.entityId,
                        textFilter,
                        workerId,
                        tags,
                        isActual,
                        isLost,
                        ctx.clientSearch,
                        ctx.holdingSearch,
                        count,
                        2,
                        skipFrom
                    )
                } else if (ctx.type === 'project') {
                    if (ctx.entityId == null) return
                    result = await JobsApi.getJobsByProject(
                        ctx.entityId,
                        textFilter,
                        workerId,
                        tags,
                        isActual,
                        isLost,
                        count,
                        2,
                        skipFrom
                    )
                } else {
                    result = await JobsApi.getJobs(
                        textFilter,
                        workerId,
                        tags,
                        isActual,
                        isLost,
                        count,
                        2,
                        skipFrom
                    )
                }
                if (result.length === 0) {
                    this.hasMoreAbove = false
                } else {
                    this.jobs.unshift(...result)
                    if (result.length < count) {
                        this.hasMoreAbove = false
                    }
                }
            } catch (e) {
                console.error(e)
                this.hasMoreAbove = false
            } finally {
                this.isLoadingMore = false
            }
        },
        async createJob(requestModel: NewJob, currentWorkerId: number): Promise<Job[]> {
            try {
                const ctx = this.context
                const payload: NewJob = { ...requestModel }
                if (ctx.type === 'client' && ctx.entityId != null) {
                    payload.ClientId = ctx.entityId
                } else if (ctx.type === 'project' && ctx.entityId != null) {
                    payload.ProjectId = ctx.entityId
                }
                const response = await JobsApi.createJob(payload)
                this.addNewJobToList(response, currentWorkerId)
                return response
            } catch (e) {
                console.error(e)
                return []
            }
        },
        addNewJobToList(newJobs: Job[], currentWorkerId: number) {
            newJobs.forEach(job => this.jobs.unshift(job))
        },
        async getJobWorkers() {
            this.workers = await WorkerApi.getWorkerAndDepartmentsList()
        },
        async attachJobFileFromDevice(
            file: any,
            jobId: number,
            opts?: { onProgress?: (percent: number) => void; signal?: AbortSignal }
        ) {
            const formData = new FormData();
            formData.append('File', file)
            formData.append('Filename', file.name)
            const response = await JobsApi.attachFile(jobId, formData, opts)
            const updatedJob = this.jobs.find(job => job.Id === jobId)
            if (updatedJob) {
                const newFile = {
                    Id: response.Id,
                    Name: response.Name,
                    Signed: response.Signed,
                    Editable: response.Editable ?? true,
                }
                if (updatedJob.Files) {
                    updatedJob.Files = [...updatedJob.Files, newFile]
                } else {
                    updatedJob.Files = [newFile]
                }
            }
            notifyFileJobBindingAdded({
                fileId: response.Id,
                jobId,
                fileCreated: true,
                projectId: updatedJob?.Project?.Id,
                clientIds: updatedJob?.Clients?.map(client => client.Id) ?? [],
            })
        },
        async attachJobFileFromClientOrProject(jobId: number, fileId: number, file: JobFile) {
            await JobsApi.attachFileFromPojectOrClient(jobId, { id: fileId})
            const updatedJob = this.jobs.find(job => job.Id === jobId)
            if (updatedJob) {
                if (updatedJob.Files) {
                    updatedJob.Files = [...updatedJob.Files, file]
                } else {
                    updatedJob.Files = [file]
                }
            }
            notifyFileJobBindingAdded({fileId, jobId})
        },
        async detachJobFile(jobId: number, fileId: number) {
            await JobsApi.dettachFile(jobId, { id: fileId })
            const updatedJob = this.jobs.find(job => job.Id === jobId)
            if (updatedJob && updatedJob.Files) {
                updatedJob.Files = updatedJob.Files?.filter(file => file.Id !== fileId)
            }
        },
        removeDeletedFileFromJobs(fileId: number, jobIds: number[]) {
            if (!jobIds?.length) {
                return
            }

            jobIds.forEach(jobId => {
                const job = this.jobs.find(j => j.Id === jobId)
                if (job?.Files) {
                    job.Files = job.Files.filter(f => f.Id !== fileId)
                }
            })
        },
        async attachTagToJob(jobId: number, name: string) {
            try {
                const updatedTags = await TagsApi.addTag({
                    EditingTagId: 0,
                    Name: name,
                    EntityType: TagEntityType.Job,
                    EntityId: jobId,
                })
                if (!Array.isArray(updatedTags)) return

                const job = this.jobs.find(j => j.Id === jobId)
                if (job) {
                    job.Tags = updatedTags as unknown as Tag[]
                }
            } catch (e) {
                console.error(e)
            }
        },
        async detachTagFromJob(jobId: number, tagId: number) {
            try {
                const ok = await TagsApi.deleteTag({
                    TagId: tagId,
                    EntityType: TagEntityType.Job,
                    EntityId: jobId,
                })
                if (!ok) return

                const job = this.jobs.find(j => j.Id === jobId)
                if (job?.Tags) {
                    job.Tags = job.Tags.filter(t => t.Id !== tagId)
                }
            } catch (e) {
                console.error(e)
            }
        },
        setUserForUpdateJobsList(userId: number | null) {
            this.showUserJobsId = userId
        },
        requestJobsRefresh(type: JobsContextType, entityId: number | null) {
            this.jobsRefreshRequest = { type, entityId }
        },
        clearJobsRefreshRequest() {
            this.jobsRefreshRequest = null
        },
        async changeJobStatus(jobId: number, action: 'endByWorker' | 'sendToChecking' | 'sendToReworking' | 'endByCreator' | 'endByAdmin', message?: string) {
            try {
                let modifiedJob = null
                switch (action) {
                    case 'endByWorker':
                        modifiedJob = await JobsApi.endByWorker(jobId, message)
                        break
                    case 'sendToChecking':
                        modifiedJob = await JobsApi.sendToChecking(jobId, message!)
                        break
                    case 'sendToReworking':
                        modifiedJob = await JobsApi.sendToReworking(jobId, message!)
                        break
                    case 'endByCreator':
                        modifiedJob = await JobsApi.endByCreator(jobId)
                        break
                    case 'endByAdmin':
                        modifiedJob = await JobsApi.endByAdmin(jobId, message)
                        break
                }
                await this.refreshJob(modifiedJob)
            } catch (e) {
                console.error(e)
            }
        },
        async refreshJob(modifiedJob: Job) {
            try {
                // const updatedJob = await JobsApi.getOne(jobId)
                const index = this.jobs.findIndex(job => job.Id === modifiedJob.Id)
                if (index !== -1) {
                    this.jobs[index] = modifiedJob
                }
            } catch (e) {
                console.error(e)
            }
        },
        async deleteJob(jobId: number) {
            try {
                await JobsApi.deleteJob(jobId)
                this.jobs = this.jobs.filter(job => job.Id !== jobId)
            } catch (e) {
                console.error(e)
            }
        },
        async editJob(data: { JobId: number, WorkerId?: number, NeedChecking?: boolean, ToDoDate?: string | null, Message?: string, Tags?: string[] }) {
            try {
                const job = await JobsApi.editJob(data)
                await this.refreshJob(job)
            } catch (e) {
                console.error(e)
            }
        }
    },
    getters: {
        jobsLoading(state) {
            return state.isJobsLoading
        },
        jobsCounter(state) {
            return state.jobsCount
        },
        allJobs(state) {
            return state.jobs
        },
        jobWorkers(state): WorkerListItem[] | null {
            return state.workers
        },
        userForUpdateJobsList(state) {
            return state.showUserJobsId
        }
    }
})


export type JobsCount = {
    Actual: number,
    Lost: number
}

export type SimpleName = {
    Id: number,
    Name: string,
}

export type JobAddition = {
    Message: string,
    Username: string,
    Date: string,
    Title: string
}

export type JobFile = {
    Id: number,
    Name: string,
    Signed: boolean,
    Editable?: boolean
}

export type Job = {
    Id: number,
    Creator: SimpleName,
    Worker: SimpleName,
    Clients: SimpleName[],
    Project: SimpleName,
    Tags: Tag[],
    Files: JobFile[] | null,
    CreateTime: string,
    ToDoDate: string,
    Status: number,
    NeedChecking: boolean,
    Lost: boolean,
    Changeable: boolean,
    Message: string,
    Additions: JobAddition[],
    CompletionDate: string,
    FinishedByAdmin: boolean,
    AdminFinisher: string
}

export type WorkerListItem = {
    IsWorker: boolean,
    Position: number,
    Worker: WorkerListItemCred | null,
    Departament: {
        Id: number,
        Name: string,
        Workers: WorkerListItemCred[] | null
    }
}

export type WorkerListItemCred = {
    Id: number,
    Name: string,
    Status: string
}

export type NewJob = {
    WorkerIds: number[] | null,
    NeedChecking: boolean,
    ToDoDate: string | null,
    Message: string,
    Tags?: string[],
    ClientId?: number,
    ProjectId?: number
}

export type JobInfo = {
    Job: Job,
    HasAccessToAnalytics: boolean,
    AnalyticsLists: { Id: number, Name: string, Position: number }[]
}
